import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { Config, Model, ConfigContextType } from "../types";

const ConfigContext = createContext<ConfigContextType | undefined>(undefined);

export const useConfig = (): ConfigContextType => {
  const context = useContext(ConfigContext);
  if (!context) {
    throw new Error("useConfig must be used within a ConfigProvider");
  }
  return context;
};

interface ConfigProviderProps {
  children: ReactNode;
  config: Config | null;
}

export const ConfigProvider: React.FC<ConfigProviderProps> = ({
  children,
  config: initialConfig,
}) => {
  const [config, setConfig] = useState<Config | null>(initialConfig);
  const [models, setModels] = useState<Model[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<string>("");
  const [visibleModels, setVisibleModels] = useState<Set<string>>(new Set());
  const [updateTrigger, setUpdateTrigger] = useState<number>(0);

  useEffect(() => {
    if (initialConfig) {
      setConfig(initialConfig);

      // Flatten all models from all groups, sorted by sortPosition
      const allModels: Model[] = [];
      const sortedGroups = [...initialConfig.modelGroups].sort(
        (a, b) => a.sortPosition - b.sortPosition
      );

      sortedGroups.forEach((group) => {
        const sortedModels = [...group.models].sort(
          (a, b) => a.sortPosition - b.sortPosition
        );
        allModels.push(...sortedModels);
      });

      setModels(allModels);

      // Set first group as selected by default
      if (sortedGroups.length > 0) {
        setSelectedGroup(sortedGroups[0].name);
      }

      // Initialize visible models
      const visible = new Set(
        allModels.filter((model) => model.isVisible).map((model) => model.id)
      );
      setVisibleModels(visible);
    }
  }, [initialConfig]);

  const updateModelVisibility = async (
    modelId: string,
    isVisible: boolean
  ): Promise<void> => {
    setModels((prevModels) => {
      const newModels = prevModels.map((model) => {
        if (model.id === modelId) {
          const updatedModel = { ...model, isVisible };
          return updatedModel;
        }
        return model;
      });

      // Process dependencies after updating the target model
      const targetModel = newModels.find((m) => m.id === modelId);
      if (
        targetModel &&
        isVisible &&
        targetModel.dependencies &&
        targetModel.dependencies.length > 0
      ) {
        return newModels.map((model) => {
          if (targetModel.dependencies.includes(model.id)) {
            return { ...model, isVisible: false };
          }
          return model;
        });
      }

      return newModels;
    });

    // Update config to reflect changes
    setConfig((prevConfig) => {
      if (!prevConfig) return prevConfig;

      return {
        ...prevConfig,
        modelGroups: prevConfig.modelGroups.map((group) => ({
          ...group,
          models: group.models.map((model) => {
            if (model.id === modelId) {
              return { ...model, isVisible };
            }

            // Check if this model is a dependency
            const targetModel = models.find((m) => m.id === modelId);
            if (
              targetModel &&
              isVisible &&
              targetModel.dependencies?.includes(model.id)
            ) {
              return { ...model, isVisible: false };
            }

            return model;
          }),
        })),
      };
    });

    // Update visible models set
    setVisibleModels((prev) => {
      const newVisible = new Set(prev);
      const targetModel = models.find((m) => m.id === modelId);

      if (isVisible) {
        newVisible.add(modelId);
        // Remove dependent models from visible set
        if (targetModel?.dependencies) {
          targetModel.dependencies.forEach((depId) => {
            newVisible.delete(depId);
          });
        }
      } else {
        newVisible.delete(modelId);
      }
      return newVisible;
    });

    // Принудительно триггерим обновление компонентов
    setUpdateTrigger((prev) => prev + 1);
  };

  const updateModelOpacity = async (
    modelId: string,
    opacity: number
  ): Promise<void> => {
    setModels((prevModels) => {
      const newModels = prevModels.map((model) => {
        if (model.id === modelId) {
          return { ...model, opacity };
        }
        return model;
      });
      return newModels;
    });

    // Update config to reflect changes
    setConfig((prevConfig) => {
      if (!prevConfig) return prevConfig;

      return {
        ...prevConfig,
        modelGroups: prevConfig.modelGroups.map((group) => ({
          ...group,
          models: group.models.map((model) =>
            model.id === modelId ? { ...model, opacity } : model
          ),
        })),
      };
    });

    // Принудительно триггерим обновление компонентов
    setUpdateTrigger((prev) => prev + 1);
  };

  const getFilteredModels = (): Model[] => {
    if (!selectedGroup || !config) return [];

    const group = config.modelGroups.find((g) => g.name === selectedGroup);
    if (!group) return [];

    return [...group.models].sort((a, b) => a.sortPosition - b.sortPosition);
  };

  const getVisibleModelsInGroup = (): Model[] => {
    return getFilteredModels().filter((model) => model.isVisible);
  };

  const getModelGroups = (): string[] => {
    if (!config) return [];
    return [...config.modelGroups]
      .sort((a, b) => a.sortPosition - b.sortPosition)
      .map((group) => group.name);
  };

  return (
    <ConfigContext.Provider
      value={{
        config,
        models,
        selectedGroup,
        setSelectedGroup,
        visibleModels,
        updateModelVisibility,
        updateModelOpacity,
        getFilteredModels,
        getVisibleModelsInGroup,
        getModelGroups,
        updateTrigger,
      }}
    >
      {children}
    </ConfigContext.Provider>
  );
};
